import {
  FormControl,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  styled,
  Stack,
  Box,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';

import { useBackupVaultColorUpdate } from './useBackupVaultColorUpdate';
import { VaultAccountBucketSelector } from './vaultAccountBucketSelector';
import { VaultAdvanceOptions } from './vaultAdvanceOptions';
import {
  VaultManagedTypeSelect,
  type VaultManagedType,
} from './vaultManagedTypeSelect';

import { VXButtons } from '../buttons/vxButtons';
import { PanelWrapper } from '../panels/panelWrapper';
import { useSupportedRegions } from '../regions/supportedRegions';

const ColorRadioButton = styled(Radio)<{ value: string }>((props) => ({
  display: 'inline',
  width: '20px',
  height: '20px',
  backgroundColor: props.value,
  margin: '6px',
  '&:first-child': {
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: props.value,
    opacity: 0.7,
  },
  '& svg': { display: 'none' },
  '&.Mui-checked': {
    border: '2px solid gray',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

export const VaultColors = [
  '#FEB2F1',
  '#8AE3FF',
  '#BDBEF7',
  '#E3B2A1',
  '#D1B9C0',
  '#A1FFD8',
];

export function CreateBackupVaultPanel(props: {
  entity: BackupVault;
  onChange: () => void;
  onClose: () => void;
}) {
  const [vaultType, setVaultType] = useState<VaultManagedType | undefined>();
  const [data, setData] = useState(props.entity);
  const [error, setError] = useState<string | undefined>();
  const dal = useDAL();
  const [isSaving, setIsSaving] = useState(false);
  const [customKey, setCustomKey] = useState(
    !!data.vaultAttributes.aws?.encryptionKey
  );
  const supportedRegions = useSupportedRegions();
  const { updateVaultColor } = useBackupVaultColorUpdate();
  const { data: vaultAccounts, isLoading: loadingAccounts } =
    dal.vaults.getAccounts();
  const providerVaultAccounts = vaultAccounts?.accounts.filter(
    (a) => a.cloudProvider === data.vaultAttributes.cloudProvider
  );
  const supportedCloudProviders = Array.from(
    new Set(vaultAccounts?.accounts.map((a) => a.cloudProvider))
  );
  const selfManagedAccounts = providerVaultAccounts?.filter(
    (account) => account.isCustomerOwned
  );
  const eonManagedAccounts = providerVaultAccounts?.filter(
    (account) => !account.isCustomerOwned
  );
  const hasEonManaged = !!eonManagedAccounts?.length;
  const showVaultTypeSelect = !!selfManagedAccounts?.length && hasEonManaged;

  useEffect(() => {
    setVaultType(hasEonManaged ? 'eon-managed' : undefined);
  }, [hasEonManaged]);

  useEffect(() => {
    setData({
      ...data,
      vaultAccountId:
        vaultType === 'eon-managed'
          ? eonManagedAccounts?.[0]?.id || ''
          : selfManagedAccounts?.[0]?.id || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultAccounts, data.vaultAttributes.cloudProvider, vaultType]);

  useEffect(() => {
    if (vaultAccounts?.accounts.length) {
      setData({
        ...data,
        vaultAttributes: {
          ...data.vaultAttributes,
          cloudProvider: vaultAccounts.accounts[0].cloudProvider,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultAccounts]);

  function validate(): boolean {
    if (!data.name || data.name.length < 5) {
      setError('Name must be at least 5 characters long');
      return false;
    }

    const regexp = /^[a-zA-Z0-9-_ ]+$/;
    if (data.name.search(regexp) === -1) {
      setError('Name can only contain letters, numbers, and hyphens');
      return false;
    }

    if (!data.vaultAttributes.cloudProvider) {
      setError('Please select a cloud provider');
      return false;
    }

    if (!data.region) {
      setError('Please select a region');
      return false;
    }

    return true;
  }

  function clearValidationError() {
    setError(undefined);
  }

  async function save() {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const res = await dal.vaults.create(data);
      data.id = res?.vault?.id || '';
    } catch (e) {
      const err = e as { code: number; body: { error: string } };
      setIsSaving(false);
      const message = err.code === 403 ? err.body.error : '';
      setError(`Failed to create backup vault ${message}`);
      return;
    }

    await updateVaultColor(data);

    props.onChange();
  }

  const resetVaultType = () => {
    setVaultType(hasEonManaged ? 'eon-managed' : undefined);
  };

  return (
    <>
      <PanelWrapper
        header={{
          onClose: props.onClose,
          title: 'Create vault',
        }}
        footer={{
          primaryButton: {
            disabled:
              isSaving ||
              data.name?.length < 5 ||
              !data.region ||
              !data.vaultAttributes.cloudProvider ||
              (customKey &&
                !data.vaultAttributes.aws?.encryptionKey &&
                data.vaultAttributes.cloudProvider === Provider.Aws),
            'data-testid': 'save-vault',
            text: 'Create Vault',
            onClick: () => {
              void save();
            },
          },
          secondaryButton: {
            'data-testid': 'cancel-vault',
            disabled: isSaving,
            text: 'Cancel',
            onClick: props.onClose,
          },
          children: (
            <Typography variant='body1'>
              Once the vault is created, only its name and color can be changed.
            </Typography>
          ),
        }}
      >
        <Stack
          marginY='40px'
          alignItems='center'
          justifyContent='center'
          flexGrow={1}
        >
          <Stack
            width='840px'
            gap='60px'
            sx={{ overflowX: 'hidden', overflowY: 'scroll' }}
          >
            <Stack gap='40px'>
              <Stack direction='row' gap='40px'>
                <FormControl className='w-1/2'>
                  <FormLabel>{'Name'}</FormLabel>
                  <TextField
                    InputProps={{
                      sx: {
                        height: '48px',
                        padding: '8px',
                      },
                    }}
                    disabled={isSaving}
                    defaultValue={data.name}
                    inputProps={{ 'data-testid': 'vault-name' }}
                    onChange={(event) => {
                      clearValidationError();
                      setData({
                        ...data,
                        name: event.target.value,
                      });
                    }}
                  />
                </FormControl>
                <FormControl className='w-1/2 flex flex-row items-center justify-between'>
                  <Stack>
                    <FormLabel>{'Color'}</FormLabel>
                    <VaultColorsSelector
                      data={data}
                      setData={setData}
                      disabled={isSaving}
                    />
                  </Stack>
                </FormControl>
              </Stack>
              <Stack direction='row' gap='40px'>
                <FormControl className='w-1/2'>
                  <FormLabel>Cloud</FormLabel>
                  <Select
                    sx={{ height: '48px', paddingLeft: '8px' }}
                    disabled={isSaving}
                    value={data.vaultAttributes.cloudProvider}
                    data-testid='vault-cloud-provider'
                    onChange={(event) => {
                      clearValidationError();
                      resetVaultType();
                      setData({
                        ...data,
                        region: '',
                        vaultAttributes: {
                          cloudProvider: event.target.value as Provider,
                        },
                      });
                    }}
                  >
                    {supportedCloudProviders.map((cloud) => (
                      <MenuItem
                        key={cloud}
                        value={cloud}
                        data-testid={`vault-cloud-provider-${cloud}`}
                      >
                        {CloudProviders[cloud].displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl className='w-1/2'>
                  <FormLabel>{'Region'}</FormLabel>
                  <Select
                    sx={{ height: '48px', paddingLeft: '8px' }}
                    disabled={isSaving || loadingAccounts}
                    data-testid='vault-region'
                    value={data.region}
                    onChange={(event) => {
                      clearValidationError();
                      setData({ ...data, region: event.target.value });
                      resetVaultType();
                    }}
                  >
                    {supportedRegions[data.vaultAttributes.cloudProvider]?.map(
                      (region, i) => {
                        return (
                          <MenuItem
                            value={region}
                            key={i}
                            data-testid={`vault-region-${region}`}
                          >
                            {region}
                          </MenuItem>
                        );
                      }
                    )}
                  </Select>
                </FormControl>
              </Stack>
              {showVaultTypeSelect && (
                <Stack direction='row' gap='40px'>
                  <Stack className='w-1/2'>
                    <VaultManagedTypeSelect
                      title='Type'
                      region={data.region}
                      regionRequired={true}
                      selfManagedVaultAccounts={selfManagedAccounts}
                      showEonManaged={hasEonManaged}
                      restrictedCloudProviders={[
                        data.vaultAttributes.cloudProvider,
                      ]}
                      vaultManagedType={vaultType}
                      onChange={(vaultType) => {
                        setVaultType(vaultType);
                      }}
                    />
                  </Stack>
                  <Stack className='w-1/2'>
                    {vaultType === 'self-managed-AWS' && (
                      <VaultAccountBucketSelector
                        data={data}
                        setData={setData}
                        vaultAccounts={
                          providerVaultAccounts?.filter(
                            (a) => a.isCustomerOwned
                          ) || []
                        }
                      />
                    )}
                  </Stack>
                </Stack>
              )}
            </Stack>
            {data.vaultAttributes.cloudProvider === Provider.Aws && (
              <VaultAdvanceOptions
                disabled={isSaving || loadingAccounts}
                data={data}
                setData={setData}
                customKey={customKey}
                setCustomKey={setCustomKey}
              />
            )}
            {error && (
              <Box sx={{ height: '25px', marginTop: '12px' }}>
                <Typography color='error' variant='body2'>
                  <i className='clarity-error-line text-[22px] text-error align-middle mb-[2px]' />
                  {error}
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </PanelWrapper>
    </>
  );
}

const VaultColorsSelector = ({
  data,
  setData,
  disabled,
}: {
  data: BackupVault;
  setData: (data: BackupVault) => void;
  disabled: boolean;
}) => {
  return (
    <RadioGroup
      className='flex-row'
      aria-labelledby='demo-controlled-radio-buttons-group'
      name='controlled-radio-buttons-group'
      value={data.backgroundColor || VaultColors[0]}
      onChange={(event) => {
        setData({
          ...data,
          backgroundColor: event.target.value,
        });
      }}
    >
      {VaultColors.map((color) => (
        <ColorRadioButton
          data-testid={`vault-color-btn`}
          key={color}
          value={color}
          inputProps={{
            ['data-testid' as string]: `vault-color-${color}`,
          }}
          disabled={disabled}
        />
      ))}
    </RadioGroup>
  );
};

export const VaultColorSelectorWithButtons = ({
  initialData,
  saveData,
  onCancel,
  disabled,
}: {
  initialData: BackupVault;
  saveData: (data: BackupVault) => void;
  onCancel: () => void;
  disabled: boolean;
}) => {
  const [data, setData] = useState(initialData);
  const onApprove = () => {
    saveData({ ...data });
  };

  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      <VaultColorsSelector data={data} setData={setData} disabled={disabled} />
      <VXButtons
        onApprove={onApprove}
        onCancel={onCancel}
        approveDisabled={disabled}
      />
    </Stack>
  );
};
